// src/pages/Address/Address.jsx
import "./Address.scss";
import { useState, useEffect } from "react";
import AddIcon from "@mui/icons-material/AddBoxOutlined";
import LocationIcon from "./../../assets/images/location-icon.png";
import EditIcon from "./../../assets/images/edit-icon.png";
import DeleteIcon from "./../../assets/images/delete-icon.png";
import CircularProgress from "@mui/material/CircularProgress";
import Map from "../../components/Map/Map";
import Loader from "../../components/Loader/Loader";
import { useAuth } from "../../context/AuthContext";
import api from "../../utils/api";
import { toast } from "react-toastify";
import { useLanguage } from "../../context/LanguageContext";

const Address = () => {
  const [openMap, setOpenMap] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [deletingAddresses, setDeletingAddresses] = useState([]);
  const [editingAddress, setEditingAddress] = useState(null);
  const { updateUserData, user } = useAuth();
  const { t } = useLanguage();

  useEffect(() => {
    if (user && user.address) {
      setAddresses(user.address);
      setIsLoading(false);
    }
  }, [user]);

  const handleDeleteAddress = async (addressId) => {
    try {
      // Add the addressId to deletingAddresses to show loader
      setDeletingAddresses((prev) => [...prev, addressId]);

      // Make API call to delete address
      const response = await api.patch(`/users/deleteAddress/${addressId}`);

      updateUserData(response?.data?.data);

      // Show success message
      toast.success(t("addressDeletedSuccess"));
    } catch (error) {
      console.error("Error deleting address:", error);
      toast.error(error.response?.data?.message || t("addressDeleteError"));
    } finally {
      // Remove the addressId from deletingAddresses
      setDeletingAddresses((prev) => prev.filter((id) => id !== addressId));
    }
  };

  const handleEditAddress = (address) => {
    // Set the address being edited
    setEditingAddress(address);
    // Open the map modal with edit mode
    setOpenMap(true);
  };

  const handleMapClose = () => {
    setOpenMap(false);
    // Clear the editing address when map is closed
    setEditingAddress(null);
  };

  return (
    <div className="address">
      <div className="container">
        <h2 className="section-title">{t("addresses")}</h2>

        {isLoading ? (
          <Loader text={t("loadingAddresses")} />
        ) : (
          <>
            <div className="address-header" onClick={() => setOpenMap(true)}>
              <AddIcon className="icon" />
              <p className="add-address">{t("addNewAddress")}</p>
            </div>

            <span className="line" />

            <div className="address-container">
              {addresses && addresses.length > 0 ? (
                addresses.map((item) => (
                  <div className="address-item" key={item._id}>
                    <div className="right">
                      <img src={LocationIcon} alt="" />
                      <div className="text-container">
                        <p>{item.name}</p>
                        <span>
                          {item.city}, {item.region}, {item.street},{" "}
                          {t("building")} {item.buildingNo}, {t("floor")}{" "}
                          {item.floorNo}
                          {item.addressSign ? ` - ${item.addressSign}` : ""}
                        </span>
                      </div>
                    </div>
                    <div className="left">
                      {deletingAddresses.includes(item._id) ? (
                        <div className="delete-loader">
                          <CircularProgress size={24} color="secondary" />
                        </div>
                      ) : (
                        <img
                          src={DeleteIcon}
                          alt={t("delete")}
                          onClick={() => handleDeleteAddress(item._id)}
                          className="delete-icon"
                        />
                      )}
                      <img
                        src={EditIcon}
                        alt={t("edit")}
                        onClick={() => handleEditAddress(item)}
                      />
                    </div>
                  </div>
                ))
              ) : (
                <div className="empty-addresses">
                  <p>{t("noAddresses")}</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
      <Map
        open={openMap}
        onClose={handleMapClose}
        editAddress={editingAddress}
      />
    </div>
  );
};

export default Address;
